// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

// MUI
import Grid from '@mui/material/Grid';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { Alert } from '@mui/material';

// Core
import { ParameterValues, paramRepository, ResourceTextApplication } from '../../../core/resources';
import { IFormValidator } from '../../../core/FormValidator';

// Common
import { ADropdown, ADropdownItemProps, ATextInput } from '../../../common/inputs';
import {
    ADialog,
    ADialogActions,
    ADialogButtonsProp,
    ADialogContent,
    ADialogContentProps,
    ADialogTitle,
} from '../../../common/dialog';
import { ASearchIconButton } from '../../../common/buttons';

// Interfaces
import { GUIDType } from '../../../interfaces/types';
import IContactPersonRecipient from '../interfaces/IContactPersonRecipient';
import { ICompanyContactPersons } from '../../../interfaces/IBusinessEntities';

// API
import companyApi from '../../../api/companyApi';

// Communication feature
import SearchRecipientsResult from './SearchRecipientsResult';
import { capitalizeFirstLetter, resolveContactPersonRole } from '../helpers/helperFunctions';
import ConfirmAddRecipientDialog from './ConfirmAddRecipientDialog';

export interface ISelectedRecipient {
    rowId: number;
    personId: string;
    companyId: string;
}

export interface ISearchResult {
    CompanyId: string;
    Id: string;
    CompanyName: string;
    Name: string;
    Title: string;
    rowId: number;
}

export interface ISearchRecipientsDialogProps {
    handleSaveRecipientsFromDialog: (recipients: ISelectedRecipient[]) => void;
    onClose: () => void;
    snellmanContactPersons: IContactPersonRecipient[];
    validator: IFormValidator;
    relatedFarmId?: string;
}

export default function SearchRecipientsDialog(props: ISearchRecipientsDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaCommunication']);
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const [selectedRecipients, setSelectedRecipients] = useState<number[]>([]);
    const [selectedRecipientType, setSelectedRecipientType] = useState<string>('');
    const [searchResults, setSearchResults] = useState<ISearchResult[] | IContactPersonRecipient[]>(
        []
    );

    const [companySearchDetails, setCompanySearchDetails] = useState<ICompanyContactPersons[]>([]);
    const [recipientSearchTypes, setRecipientSearchTypes] = useState<ParameterValues>([]);
    const [recipientTypeProps, setRecipientTypeProps] = useState<ADropdownItemProps[]>([]);
    const [farmCompanyTitles, setFarmCompanyTitles] = useState<ParameterValues>([]);
    const [logisticsCompanyTitles, setLogisticsCompanyTitles] = useState<ParameterValues>([]);
    const [snellmanCompanyTitles, setSnellmanCompanyTitles] = useState<ParameterValues>([]);
    const [selectedProducerCompanyId, setSelectedProducerCompanyId] = useState<string>(
        props.relatedFarmId ?? ''
    );
    const [companySelectionError, setCompanySelectionError] = useState<string | null>(null);

    useEffect(() => {
        paramRepository.load(['AnelmaCommunication', 'AnelmaGeneral']).then(() => {
            setRecipientSearchTypes(
                paramRepository.resource('AnelmaCommunication', 'RecipientSearchTypes')
            );
            setFarmCompanyTitles(paramRepository.resource('AnelmaGeneral', 'FarmTitles'));
            setLogisticsCompanyTitles(paramRepository.resource('AnelmaGeneral', 'LogisticsTitles'));
            setSnellmanCompanyTitles(paramRepository.resource('AnelmaGeneral', 'SnellmanTitles'));
        });
    }, []);

    useEffect(() => {
        if (recipientSearchTypes.length > 0) {
            const constructedRecipientTypes = recipientSearchTypes.map((type) => ({
                value: type.code,
                text: type.text,
            }));

            setRecipientTypeProps(constructedRecipientTypes);
            setSelectedRecipientType('0');
        }
    }, [recipientSearchTypes]);

    useEffect(() => {
        setSearchTerm('');
        setSearchResults([]);
    }, [selectedRecipientType]);

    useEffect(() => {
        if (companySearchDetails.length > 0) {
            const actualSearchResult: ISearchResult[] = [];

            const companyDetailsArray = Array.from(companySearchDetails.values());
            let rowIndex = 0;

            companyDetailsArray.forEach((companyDetail) => {
                companyDetail.ContactPersons.forEach((contactPerson) => {
                    actualSearchResult.push({
                        CompanyId: String(contactPerson.OwnerId),
                        Id: String(contactPerson.Id),
                        CompanyName: companyDetail.CompanyName,
                        Name:
                            contactPerson.Name !== undefined && contactPerson.Name.length > 0
                                ? contactPerson.Name
                                : 'Nimetön henkilö',
                        Title:
                            contactPerson.Roles.length > 0
                                ? resolveContactPersonRole(
                                      companyDetail.CompanyType,
                                      contactPerson.Roles[0].Type,
                                      farmCompanyTitles,
                                      logisticsCompanyTitles,
                                      snellmanCompanyTitles
                                  )
                                : '-',
                        rowId: rowIndex,
                    });
                    rowIndex++;
                });
            });

            setSearchResults(actualSearchResult);
        }

        setIsLoading(false);
    }, [companySearchDetails]);

    // #region General function expressions
    const search = () => {
        if (!searchTerm) return;

        setIsLoading(true);
        setSearchResults([]);

        switch (selectedRecipientType) {
            case '0':
                searchFromCompanies(searchTerm);
                break;
            case '1':
                setSearchResults(searchFromSnellmanPersons(searchTerm));
                setIsLoading(false);
                break;
        }
    };

    const searchFromSnellmanPersons = (searchTerm: string) => {
        return props.snellmanContactPersons
            .filter((person) => {
                if (isContactPersonNameNullOrEmpty(person)) return false;
                return person.Name.toLowerCase().includes(searchTerm.toLowerCase());
            })
            .map((person, idx) => ({
                CompanyId: person.CompanyId,
                Id: person.Id,
                CompanyName: person.CompanyName,
                Name: person.Name,
                Title: person.Title ?? '-',
                rowId: idx,
            }));
    };

    // #region API function expressions
    const searchFromCompanies = (searchTerm: string) => {
        companyApi
            .searchCompaniesContactPersonsByCompanyName(searchTerm)
            .then((response) => {
                if (!response) return;

                setCompanySearchDetails(response.Items);
            })
            .catch((error) => {
                enqueueSnackbar(`${t('AnelmaGeneral:1020')}, ${error}`, { variant: 'error' });
                setIsLoading(false);
            });
    };
    // #endregion

    // #region Handlers

    const handleOnSelectedRecipientChange = (selectionModel: GridRowSelectionModel) => {
        if (selectionModel.length === 0) {
            setSelectedRecipients([]);
            setSelectedProducerCompanyId('');
            setCompanySelectionError(null);

            return;
        }

        const selectedRowIds = selectionModel as string[];

        const selectedRows = (searchResults as ISearchResult[])
            .map((row, idx) => ({
                ...row,
                rowIndex: idx,
                rowId: `${row.Id}-${row.CompanyId || idx}`,
            }))
            .filter((row) => selectedRowIds.includes(row.rowId));

        // Keraa valittujen rivien yritys-ID:t
        const uniqueCompanyIds = Array.from(new Set(selectedRows.map((r) => r.CompanyId)));

        if (uniqueCompanyIds.length > 1) {
            setCompanySelectionError(t('AnelmaCommunication:1174'));
            setSelectedRecipients([]);
            setSelectedProducerCompanyId('');
            return;
        } else {
            setCompanySelectionError(null);
        }
        setSelectedRecipients(selectedRows.map((r) => r.rowIndex));
        setSelectedProducerCompanyId(selectedRows[0]?.CompanyId ?? '');
    };

    const handleConfirmAddRecipients = () => {
        // Kaytetaan indeksiaselectedRecipients-taulukossa
        const selectedRecipientObjects: ISelectedRecipient[] = selectedRecipients.map((rowId) => {
            const result = (searchResults as ISearchResult[]).find((s) => s.rowId === rowId);

            return {
                rowId: rowId,
                personId: result?.Id ?? '',
                companyId: result?.CompanyId ?? '',
            };
        });
        props.handleSaveRecipientsFromDialog(selectedRecipientObjects);
    };
    // #endregion

    const isContactPersonNameNullOrEmpty = (person: IContactPersonRecipient) => {
        return person.Name === '' || person.Name === null;
    };

    const handleSnackbarMessage = (
        message: string,
        variant: 'success' | 'error' | 'warning' | 'info'
    ) => {
        enqueueSnackbar(message, {
            variant: variant,
        });
    };

    const footerActionButtons: ADialogButtonsProp = {
        left: [
            {
                onClick: () => props.onClose(),
                type: 'cancel',
            },
        ],
        right: [
            {
                onClick: () => setShowConfirmDialog(true),
                disabled: selectedRecipients.length === 0,
                type: 'save',
            },
        ],
    };

    const dialogContentProps: ADialogContentProps = {
        size: 'md',
        isLoading: isLoading,
    };

    return (
        <>
            <ADialog open onClose={() => props.onClose()}>
                <ADialogTitle>{t('AnelmaCommunication:1068')}</ADialogTitle>
                <ADialogContent {...dialogContentProps}>
                    <Grid container direction='row' alignItems='stretch'>
                        <Grid size={{ sm: 2 }}>
                            <ADropdown
                                id='communication-recipient-type'
                                items={recipientTypeProps}
                                onChange={(value) => {
                                    setSelectedRecipientType(value as string);
                                }}
                                style={{ minWidth: '100px' }}
                                validator={props.validator}
                                value={selectedRecipientType}
                            />
                        </Grid>
                        <Grid size={{ sm: 8 }}>
                            <ATextInput
                                id='search-recipients-input'
                                label={
                                    selectedRecipientType === '0'
                                        ? t('AnelmaCommunication:1075')
                                        : t('AnelmaCommunication:1082')
                                }
                                onChange={(v) => setSearchTerm(v)}
                                onKeyUp={(e) => {
                                    if (e.key === 'Enter') search();
                                }}
                                validator={props.validator}
                                value={searchTerm}
                            />
                        </Grid>

                        <Grid size={{ sm: 2 }}>
                            <ASearchIconButton
                                style={{ marginTop: '12px' }}
                                onClick={() => search()}
                                type='action'
                                tooltip={capitalizeFirstLetter(t('AnelmaCommunication:1058'))}
                            ></ASearchIconButton>
                        </Grid>
                    </Grid>

                    <SearchRecipientsResult
                        rowData={searchResults}
                        handleOnSelectedRecipientChange={handleOnSelectedRecipientChange}
                    ></SearchRecipientsResult>

                    {/* Virheilmoitus nakyviin valintaruudukon alle */}
                    {companySelectionError && (
                        <Alert severity='error' style={{ marginTop: 12, marginBottom: 8 }}>
                            {companySelectionError}
                        </Alert>
                    )}
                </ADialogContent>

                <ADialogActions buttons={footerActionButtons} />
            </ADialog>

            <ConfirmAddRecipientDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirmation={handleConfirmAddRecipients}
                confirmation={t('AnelmaCommunication:1081')}
            />
        </>
    );
}
